import { UsageWindow } from "./use-org-ai-credit";

// Number of AI credit periods offered in a picker (current + prior).
export const PERIOD_COUNT = 6;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// addMonthsUTC shifts a date by whole months while pinning the anchor's
// day-of-month, clamping to the last day of shorter months the way OpenMeter's
// monthly recurrence does — never rolling into the next month. Naive
// Date arithmetic would turn "Feb 30" into "Mar 2"; here it clamps to "Feb 28".
const addMonthsUTC = (anchor: Date, months: number): Date => {
  const target = new Date(
    Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + months, 1),
  );
  const daysInMonth = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  return new Date(
    Date.UTC(
      target.getUTCFullYear(),
      target.getUTCMonth(),
      Math.min(anchor.getUTCDate(), daysInMonth),
      anchor.getUTCHours(),
      anchor.getUTCMinutes(),
      anchor.getUTCSeconds(),
    ),
  );
};

export const fmtUTCDate = (d: Date): string =>
  `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;

export interface CreditPeriod extends UsageWindow {
  label: string;
}

// buildPeriods derives the current AI credit period plus `count - 1` prior
// periods from the credit-period anchor. The current period start (current.from)
// sits on the monthly anchor grid, so we step whole months back from it, always
// landing on the anchor's day-of-month. Deriving both endpoints from the single
// anchor keeps adjacent periods contiguous — period k's start is period k+1's
// end. The current period keeps its real reset time (current.to).
//
// Note: periods that predate the entitlement's MeasureUsageFrom (before the
// customer was provisioned) can't be read back from OpenMeter and will error;
// callers should surface that gracefully.
export const buildPeriods = (
  current: UsageWindow,
  count: number,
): CreditPeriod[] => {
  const anchor = current.from;
  const periods: CreditPeriod[] = [];
  for (let k = 0; k < count; k++) {
    const from = addMonthsUTC(anchor, -k);
    const to = k === 0 ? current.to : addMonthsUTC(anchor, -(k - 1));
    const range = `${fmtUTCDate(from)} – ${fmtUTCDate(to)}`;
    periods.push({ from, to, label: k === 0 ? `Current (${range})` : range });
  }
  return periods;
};
